import http from '@utils/http';

/**
 * GET /api/v1/open-market/leads
 * @param {{ page?: number, limit?: number }} params
 */
export const getOpenMarketLeads = (params = {}) =>
    http.get('/open-market/leads', { params }).then((res) => {
        const payload = res.data?.data ?? {};
        return {
            items: payload?.items ?? [],
            total: payload?.total ?? 0,
            totalPages: payload?.totalPages ?? 1,
        };
    });

/**
 * GET /api/v1/open-market/leads/:id
 */
export const getOpenMarketLeadById = (id) =>
    http.get(`/open-market/leads/${id}`).then((res) => res.data?.data ?? res.data);

/**
 * GET /api/v1/open-market/leads/:leadId/bids
 */
export const getLeadBids = (leadId) =>
    http.get(`/open-market/leads/${leadId}/bids`).then((res) => {
        const data = res.data?.data ?? res.data;
        return Array.isArray(data) ? data : [];
    });

/**
 * POST /api/v1/open-market/leads
 * @param {{
 *   title: string,
 *   description: string,
 *   budgetMin: number,
 *   budgetMax: number,
 *   category: string
 * }} body
 */
export const createOpenMarketLead = (body) =>
    http.post('/open-market/leads', body).then((res) => res.data?.data ?? res.data);

/**
 * POST /api/v1/open-market/leads/:leadId/bids
 * @param {string} leadId
 * @param {{ amount: number, proposal: string }} body
 */
export const placeBid = (leadId, body = {}) =>
    http.post(`/open-market/leads/${leadId}/bids`, body).then((res) => res.data?.data ?? res.data);

/**
 * GET /api/v1/open-market/bids/:bidId
 */
export const getBidById = (bidId) => http.get(`/open-market/bids/${bidId}`).then((res) => res.data?.data ?? res.data);

/**
 * PATCH /api/v1/open-market/bids/:bidId (e.g. approve: { status: 'accepted' }, decline: { status: 'declined' })
 * @param {string} bidId
 * @param {{ status?: string }} body
 */
export const updateBid = (bidId, body = {}) =>
    http.patch(`/open-market/bids/${bidId}`, body).then((res) => res.data?.data ?? res.data);

/**
 * POST /api/v1/open-market/bids/:bidId/start-chat
 * @param {string} bidId
 */
export const startChat = (bidId) =>
    http.post(`/open-market/bids/${bidId}/start-chat`).then((res) => res.data?.data ?? res.data);

/**
 * PATCH /api/v1/open-market/leads/:leadId – set lead state (running | paused)
 * @param {string} leadId
 * @param {{ state: 'running' | 'paused' }} body
 */
export const patchLeadState = (leadId, body) =>
    http.patch(`/open-market/leads/${leadId}`, body).then((res) => res.data?.data ?? res.data);

/**
 * GET /api/v1/project-requests – list project requests (paginated)
 * @param {{ page?: number, limit?: number }} params
 * @returns {{ items: array, total: number, page: number, limit: number, totalPages: number }}
 */
export const getProjectRequests = (params = {}) =>
    http.get('/project-requests', { params }).then((res) => {
        const payload = res.data ?? {};
        const data = Array.isArray(payload.data) ? payload.data : [];
        return {
            items: data,
            total: payload.total ?? 0,
            page: payload.page ?? 1,
            limit: payload.limit ?? 10,
            totalPages: payload.totalPages ?? 1,
        };
    });

/**
 * PATCH /api/v1/project-requests/:requestId/respond – approve or reject a project request
 * @param {string} requestId
 * @param {{ action: 'approved'|'rejected', note?: string }} body
 */
export const respondToProjectRequest = (requestId, body) =>
    http.patch(`/project-requests/${requestId}/respond`, body).then((res) => res.data?.data ?? res.data);

/**
 * POST /api/v1/project-requests – create project from accepted open market bid
 * @param {{ openMarketLeadId: string, openMarketBidId: string }} body
 */
export const createProjectRequest = (body) =>
    http.post('/project-requests', body).then((res) => res.data?.data ?? res.data);

/**
 * POST /api/v1/project-requests/:requestId/create-project
 * @param {string} requestId
 * @param {{ name: string, clientId: string, budget: number, priority: string }} body
 */
export const createProjectFromProjectRequest = (requestId, body) =>
    http.post(`/project-requests/${requestId}/create-project`, body).then((res) => res.data?.data ?? res.data);

/**
 * POST /api/v1/project-requests/:requestId/create-audit
 * @param {string} requestId
 * @param {{ name: string, description?: string, clientId: string }} body
 */
export const createAuditFromProjectRequest = (requestId, body) =>
    http.post(`/project-requests/${requestId}/create-audit`, body).then((res) => res.data?.data ?? res.data);
